import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import Input from '../components/Input';
import Button from '../components/Button';
import TurnstileWidget from '../components/TurnstileWidget';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleToken = useCallback((token: string) => setTurnstileToken(token), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email, turnstileToken });
      setSent(true);
    } catch { setSent(true); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-2xl font-bold mb-3">{t('auth.checkEmail')}</h1>
        <p className="text-muted text-sm mb-6">{t('auth.emailSent', { email })}</p>
        <Link to="/login" className="text-primary hover:text-primary-hover">{t('auth.backToLogin')}</Link>
      </div>
    );
  }

  return (
    <motion.div
      className="max-w-md mx-auto px-6 py-20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">{t('auth.resetPassword')}</h1>
        <p className="text-muted text-sm">{t('auth.resetDesc')}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <Input label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        <TurnstileWidget onToken={handleToken} />
        <Button type="submit" disabled={loading || !turnstileToken} className="w-full" size="lg">
          {loading ? t('auth.sending') : t('auth.sendResetLink')}
        </Button>
      </form>
      <p className="text-center text-sm text-muted mt-6">
        <Link to="/login" className="text-primary hover:text-primary-hover">{t('auth.backToLogin')}</Link>
      </p>
    </motion.div>
  );
}
